import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, ArrowRight, Filter, Download, CreditCard, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending_payment: "Pending payment",
  paid: "Confirmed Payment",
  in_production: "In production",
  ready_for_pickup: "Ready for pickup",
  shipped: "Shipped",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
];
const PAID_OPTIONS = [
  { value: "", label: "Any payment" },
  { value: "paid", label: "Paid" },
  { value: "on_account", label: "On Account" },
  { value: "unpaid", label: "Unpaid" },
];

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const paymentBucket = (o) => {
  if (o.status === "pending_payment") return "unpaid";
  const mode = (o.payment_details?.mode || "").toLowerCase();
  if (mode === "on_account") return "on_account";
  return "paid";
};

const MyOrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialStatus = new URLSearchParams(location.search).get("status") || "";
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [paidFilter, setPaidFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  }, []);

  // Keep URL in sync with the status filter so tile-clicks deep-link correctly
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const cur = sp.get("status") || "";
    if (cur !== statusFilter) {
      if (statusFilter) sp.set("status", statusFilter); else sp.delete("status");
      navigate({ pathname: "/dashboard/orders", search: sp.toString() }, { replace: true });
    }
  }, [statusFilter]);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (paidFilter && paymentBucket(o) !== paidFilter) return false;
      return true;
    });
  }, [orders, statusFilter, paidFilter]);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/orders/mine/export.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="my-orders-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">My account</p>
        <h1 className="font-display text-4xl font-extrabold text-[#0B1E3F] mt-2">Orders history</h1>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        {/* Header row — title + count + filters all on a single line */}
        <div className="p-5 border-b border-[#E2E8F0] flex flex-wrap items-center gap-3">
          <Package className="h-5 w-5 text-[#0B1E3F]" />
          <h2 className="font-display text-xl font-bold">My orders</h2>
          <span className="font-display font-extrabold text-2xl text-[#06B6D4]" data-testid="my-orders-count">
            {filtered.length}
          </span>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-neutral-500">
              <Filter className="h-3.5 w-3.5" /> Filter
            </div>
            <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="pav-input h-9 min-w-[180px]" data-testid="status-filter-select">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value || "_all"} value={o.value || "_all"}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={paidFilter || "_any"} onValueChange={(v) => setPaidFilter(v === "_any" ? "" : v)}>
              <SelectTrigger className="pav-input h-9 min-w-[160px]" data-testid="paid-filter-select">
                <SelectValue placeholder="Any payment" />
              </SelectTrigger>
              <SelectContent>
                {PAID_OPTIONS.map((o) => (
                  <SelectItem key={o.value || "_any"} value={o.value || "_any"}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={downloadXlsx} disabled={downloading || orders.length === 0}
              className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="export-orders-xlsx">
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Excel
            </button>
          </div>
        </div>

        {loading ? (
          <p className="p-6 text-neutral-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="orders-empty-state">
            <p className="text-neutral-600 mb-3">
              {orders.length === 0 ? "You haven't placed any orders yet." : "No orders match the selected filters."}
            </p>
            {orders.length === 0 && (
              <Link to="/order" className="btn-primary inline-flex items-center gap-2">Place your first order <ArrowRight className="h-4 w-4" /></Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#E2E8F0]">
            {filtered.map((o) => {
              const bucket = paymentBucket(o);
              return (
                <div key={o.order_id} data-testid={`order-row-${o.order_id}`}
                  className="flex flex-wrap items-center justify-between gap-4 p-5 hover:bg-[#F8FAFC] transition-colors">
                  <Link to={`/track?id=${o.order_id}`} className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{o.order_id}</span>
                      <span className={`pav-chip status-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                        bucket === "paid" ? "bg-green-100 text-green-700"
                        : bucket === "on_account" ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                      }`}>
                        {bucket === "on_account" ? "ON ACCOUNT" : bucket.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1">
                      {o.items?.length} item{o.items?.length !== 1 ? "s" : ""} • {new Date(o.created_at).toLocaleDateString()} • {o.fulfillment_type}
                    </p>
                    {o.has_layout_request && (
                      <p className="text-[11px] mt-1.5 inline-flex items-center gap-1.5 font-semibold text-amber-700"
                        data-testid={`order-layout-status-${o.order_id}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        Layout: {({ new_request: "New Request", on_process: "On Process", for_approval: "For Approval", approved: "Approved" }[o.layout_status] || o.layout_status || "New Request")}
                      </p>
                    )}
                  </Link>
                  <div className="text-right flex flex-col items-end gap-2">
                    <p className="font-display font-extrabold text-lg text-[#0B1E3F]">{peso(o.total)}</p>
                    {o.status === "pending_payment" ? (
                      <Link to={`/checkout/${o.order_id}`}
                        data-testid={`complete-payment-${o.order_id}`}
                        className="btn-accent text-xs inline-flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5" /> Complete payment
                      </Link>
                    ) : (
                      <Link to={`/track?id=${o.order_id}`} className="text-xs text-neutral-500 hover:text-[#0B1E3F]">View details →</Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;
